import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useSession } from "../../services/SessionContext";
import type { Upload } from "../../types/domain";

export function EditUploadPage() {
  const { uploadId } = useParams();
  const { repo, updateUpload } = useSession();
  if (!uploadId) return <Navigate to="/app/dashboard" replace />;
  const upload = repo.getUpload(uploadId);
  if (!upload) return <Navigate to="/app/dashboard" replace />;
  return <EditUploadForm upload={upload} updateUpload={updateUpload} />;
}

function EditUploadForm({
  upload,
  updateUpload,
}: {
  upload: Upload;
  updateUpload: ReturnType<typeof useSession>["updateUpload"];
}) {
  const navigate = useNavigate();
  const [controllerName, setControllerName] = useState(upload.controllerName ?? "");
  const [projectName, setProjectName] = useState(upload.projectName ?? "");
  const [controllerAddress, setControllerAddress] = useState(upload.controllerAddress ?? "");
  const [latitude, setLatitude] = useState(upload.latitude?.toString() ?? "");
  const [longitude, setLongitude] = useState(upload.longitude?.toString() ?? "");

  function submit(event: React.FormEvent) {
    event.preventDefault();
    updateUpload(upload.uploadId, {
      controllerName,
      projectName,
      controllerAddress,
      latitude: latitude ? Number(latitude) : undefined,
      longitude: longitude ? Number(longitude) : undefined,
    });
    navigate(`/app/uploads/${upload.uploadId}`);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-20 md:pb-0">
      <h1 className="text-2xl font-bold text-navy-950">Edit upload details</h1>
      <Card>
        <CardHeader>
          <CardTitle>{upload.originalFileName}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="project">Project name</Label>
              <Input id="project" value={projectName} onChange={(event) => setProjectName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="controller">Controller name</Label>
              <Input id="controller" value={controllerName} onChange={(event) => setControllerName(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Controller address</Label>
              <Input id="address" value={controllerAddress} onChange={(event) => setControllerAddress(event.target.value)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lat">Latitude</Label>
                <Input id="lat" inputMode="decimal" value={latitude} onChange={(event) => setLatitude(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lng">Longitude</Label>
                <Input id="lng" inputMode="decimal" value={longitude} onChange={(event) => setLongitude(event.target.value)} />
              </div>
            </div>
            <div className="flex gap-3">
              <Button type="submit">Save details</Button>
              <Button type="button" variant="secondary" onClick={() => navigate(`/app/uploads/${upload.uploadId}`)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
